import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Modal, { TextField, TextArea, SelectField } from "@/components/Modal";
import { ImageUploader } from "@/components/common";
import { toast } from "sonner";

export default function QuoteRequestModal({ product, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    product_name: product?.name || "", quantity: 1, address: "", zone: "",
    building_type: "", description: "", dimensions: "", constraints: "",
    desired_date: "", urgency: "programmer", photos: [],
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => { api.get("/zones").then((r) => setZones(r.data)); }, []);

  if (!user) {
    return (
      <Modal title="Connexion requise" onClose={onClose} testid="quote-auth-modal">
        <p className="text-sm text-slate-600 mb-4">Connectez-vous pour demander un devis.</p>
        <button onClick={() => navigate("/login")} className="w-full bg-[#C85A32] text-white font-bold py-2.5 rounded-lg" data-testid="quote-login-redirect">Se connecter</button>
      </Modal>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", { ...f, product_id: product?.id || "", quantity: Number(f.quantity) });
      toast.success("Demande de devis envoyée");
      onClose();
      navigate("/devis");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Demander un devis" onClose={onClose} testid="quote-modal" size="lg">
      <form onSubmit={submit} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Produit / service concerné" value={f.product_name} onChange={set("product_name")} required testid="quote-product" />
          <TextField label="Quantité" type="number" value={f.quantity} onChange={set("quantity")} testid="quote-qty" />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SelectField label="Zone" value={f.zone} onChange={set("zone")} testid="quote-zone" options={zones.map((z) => ({ value: z.name, label: z.name }))} />
          <SelectField label="Type de bâtiment" value={f.building_type} onChange={set("building_type")} testid="quote-building"
            options={[{ value: "maison", label: "Maison individuelle" }, { value: "appartement", label: "Appartement" }, { value: "immeuble", label: "Immeuble" }, { value: "commerce", label: "Local commercial" }, { value: "chantier", label: "Chantier" }]} />
        </div>
        <TextField label="Adresse" value={f.address} onChange={set("address")} required testid="quote-address" />
        <TextArea label="Description du besoin" value={f.description} onChange={set("description")} required testid="quote-desc" rows={3} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Dimensions (si nécessaire)" value={f.dimensions} onChange={set("dimensions")} testid="quote-dims" required={false} />
          <TextField label="Date souhaitée" type="date" value={f.desired_date} onChange={set("desired_date")} testid="quote-date" required={false} />
        </div>
        <TextArea label="Contraintes particulières" value={f.constraints} onChange={set("constraints")} testid="quote-constraints" required={false} rows={2} />
        <div>
          <span className="text-sm font-semibold text-slate-700 block mb-1.5">Photos / vidéos</span>
          <ImageUploader value={f.photos} onChange={set("photos")} testid="quote-photos" accept="image/*,video/*" />
        </div>
        <div>
          <span className="text-sm font-semibold text-slate-700">Urgence</span>
          <div className="grid grid-cols-2 gap-2 mt-1">
            <button type="button" onClick={() => set("urgency")("urgent")} data-testid="quote-urgent"
              className={`py-2.5 rounded-lg text-sm font-bold border-2 ${f.urgency === "urgent" ? "border-[#DC2626] bg-red-50 text-[#DC2626]" : "border-slate-200 text-slate-500"}`}>⚡ Urgent</button>
            <button type="button" onClick={() => set("urgency")("programmer")} data-testid="quote-programmer"
              className={`py-2.5 rounded-lg text-sm font-bold border-2 ${f.urgency === "programmer" ? "border-[#C85A32] bg-[#FDF3EE] text-[#C85A32]" : "border-slate-200 text-slate-500"}`}>📅 Programmer</button>
          </div>
        </div>
        <button disabled={busy} className="w-full bg-[#0F172A] text-white font-bold py-3 rounded-lg disabled:opacity-50" data-testid="quote-submit">
          {busy ? "Envoi..." : "Envoyer la demande de devis"}
        </button>
      </form>
    </Modal>
  );
}
